import { useState, useEffect, useRef } from "react";
import { PoundSterling, Search, Loader2, Trash2, ExternalLink, RotateCcw, Wand2, AlertTriangle, RefreshCw, Sparkles, PlusCircle, HelpCircle, Star, HeartPulse, Scale, FileWarning } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BACKEND_URL, uid, CAUTIONARY_TALES, VALUE_CATEGORIES, EVIDENCE_TEMPLATE, HAVE_OPTIONS, EVIDENCE_TYPES } from "./constants";

// --- Inlined atoms ---
function FieldWand({ tab, field, fieldLabel, current, context, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, field, fieldLabel, current, context }),
      });
      if (res.ok) { const d = await res.json(); if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); } else toast.info("No draft returned"); }
      else toast.error("Failed to draft");
    } catch (e) { toast.error("Failed to draft"); }
    setBusy(false);
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={testid} title="AI draft an answer">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      <span className="ml-1 text-xs">AI draft</span>
    </Button>
  );
}
function WandField({ tab, field, label, value, onChange, onBlur, rows = 3, placeholder, context, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <FieldWand tab={tab} field={field} fieldLabel={label} current={value} context={context} onAnswer={(a) => onChange(a)} testid={`${testid}-wand`} />
      </div>
      <AutoResizeTextarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} minRows={rows} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function CautionaryTale({ tale, testid }) {
  return (
    <Card className="border-amber-200 bg-amber-50 sticky top-4" data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Cautionary tale · {tale.title}</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-xs text-amber-900">
        <p>{tale.body}</p>
        <p className="font-semibold">Lesson: <span className="font-normal">{tale.lesson}</span></p>
        {tale.extra && <p className="pt-2 border-t border-amber-200 italic">{tale.extra}</p>}
      </CardContent>
    </Card>
  );
}
function KeyQuestionsSection({ tab, tabData, questions, onSetQuestions, persistTab, testidBase }) {
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const suggest = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/suggest-key-questions`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, tab_data: tabData, existing: questions.map((q) => q.text) }),
      });
      if (res.ok) {
        const d = await res.json();
        const next = [...questions, ...(d.questions || []).map((t) => ({ id: uid(), text: t }))];
        onSetQuestions(next); persistTab(next);
        if (d.questions?.length) toast.success(`Added ${d.questions.length} AI questions`);
        else toast.info("No suggestions returned");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const add = () => { if (!input.trim()) return; const next = [...questions, { id: uid(), text: input.trim() }]; onSetQuestions(next); persistTab(next); setInput(""); };
  const remove = (id) => { const next = questions.filter((q) => q.id !== id); onSetQuestions(next); persistTab(next); };
  return (
    <Card data-testid={`${testidBase}-section`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-teal-600" /> Key questions</CardTitle>
          <Button size="sm" variant="ghost" onClick={suggest} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={`${testidBase}-suggest`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI suggest</span>
          </Button>
        </div>
        <p className="text-xs text-slate-500">Sharp open questions you should be able to answer to defend this section.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {questions.length === 0 && <p className="text-xs text-slate-400 italic">No questions yet. Add your own or click AI suggest.</p>}
        {questions.map((q) => (
          <div key={q.id} className="flex items-start gap-2 border-l-2 border-l-teal-300 pl-3 py-1.5" data-testid={`${testidBase}-q-${q.id}`}>
            <span className="text-sm text-slate-700 flex-1 leading-snug">{q.text}</span>
            <button onClick={() => remove(q.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`${testidBase}-q-del-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-2 border-t">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add your own question…" className="text-sm h-9" data-testid={`${testidBase}-input`} />
          <Button size="sm" onClick={add} data-testid={`${testidBase}-add`}><PlusCircle className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AccessHeorTab({ data, setData, persist, companyProfile }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const persistTab = (nextAh) => persist({ access_heor: nextAh });

  const toggleCat = (id) => {
    const cur = data.value_categories || [];
    const nextAh = { ...data, value_categories: cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id] };
    setData(nextAh); persistTab(nextAh);
  };

  const evidence = (data.evidence && data.evidence.length ? data.evidence : EVIDENCE_TEMPLATE.map((e, i) => ({ id: `ev_${i}`, ...e })));
  const updateEv = (id, patch) => set({ evidence: evidence.map((e) => e.id === id ? { ...e, ...patch } : e) });
  const removeEv = (id) => { const nextAh = { ...data, evidence: evidence.filter((e) => e.id !== id) }; setData(nextAh); persistTab(nextAh); };
  const addEv = () => { const nextAh = { ...data, evidence: [...evidence, { id: `ev_${Date.now()}`, what: "", who: "", have: "no" }] }; setData(nextAh); persistTab(nextAh); };

  const runSearch = async (searchQuery, replace = false) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/clinical/search-nice`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ query: searchQuery, context: { outcome_improved: data.outcome_improved, comparator: data.comparator, best_patients: data.best_patients } }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.not_configured) toast.error("Tavily API key not set");
        else if (d.appraisals?.length) {
          const nextList = replace ? d.appraisals : [...(data.nice_appraisals || []), ...d.appraisals];
          const nextAh = { ...data, nice_appraisals: nextList };
          setData(nextAh); persistTab(nextAh);
          toast.success(d.auto_curated ? `Auto-curated ${d.appraisals.length} NICE appraisals` : `Added ${d.appraisals.length} appraisals`);
        } else toast.info(d.message || "No appraisals found");
      } else toast.error("Search failed");
    } catch (e) { toast.error("Search failed"); }
    setBusy(false);
  };

  const autoCurated = useRef(false);
  useEffect(() => {
    if (autoCurated.current) return;
    if (!companyProfile && !data.outcome_improved) return;
    if ((data.nice_appraisals || []).length > 0) return;
    autoCurated.current = true;
    setAutoLoading(true);
    runSearch("", true).finally(() => setAutoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfile]);

  const removeApp = (id) => { const nextAh = { ...data, nice_appraisals: (data.nice_appraisals || []).filter((a) => a.id !== id) }; setData(nextAh); persistTab(nextAh); };
  const resetTab = () => {
    if (window.confirm("Reset the Access & HEOR tab? This clears all answers.")) {
      const empty = { outcome_improved: "", best_patients: "", value_statement: "", value_categories: [], comparator: "", replaces_cost: "", additional_costs: "", why_payer_fund: "", evidence: [], key_questions: [], nice_appraisals: [] };
      setData(empty); persistTab(empty); toast.success("Access & HEOR reset");
    }
  };
  const refresh = () => runSearch("", true);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="access-heor-tab">
      <div className="lg:col-span-2 space-y-4">
        {/* A. Value case */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-teal-600" /> A · Value case</CardTitle>
              <Button size="sm" variant="ghost" onClick={resetTab} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="ah-reset-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
            </div>
            <p className="text-xs text-slate-500">What outcome improves, for whom — and how you would say it to a payer in one sentence.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="access_heor" field="outcome_improved" label="What outcome does the product improve?" value={data.outcome_improved} onChange={setField("outcome_improved")} onBlur={persist} context={data} testid="ah-outcome" />
            <WandField tab="access_heor" field="best_patients" label="For which patients is that improvement most valuable?" value={data.best_patients} onChange={setField("best_patients")} onBlur={persist} context={data} testid="ah-best-patients" />
            <WandField tab="access_heor" field="value_statement" label="Value statement (one sentence)" value={data.value_statement} onChange={setField("value_statement")} onBlur={persist} rows={3} placeholder="For [patient population], our product creates value by [improving outcome or reducing burden] compared with [current alternative], potentially reducing [healthcare use, treatment burden or future costs]." context={data} testid="ah-stmt" />
            <div>
              <p className="text-sm font-medium text-slate-800 mb-2">Value categories most relevant to us</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {VALUE_CATEGORIES.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 text-sm text-slate-700 hover:bg-slate-50 rounded px-2 py-1 cursor-pointer">
                    <Checkbox checked={(data.value_categories || []).includes(v.id)} onCheckedChange={() => toggleCat(v.id)} data-testid={`ah-cat-${v.id}`} /> {v.label}
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* B. Comparator */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Scale className="w-4 h-4 text-teal-600" /> B · Comparator</CardTitle>
            <p className="text-xs text-slate-500">Who / what the payer will compare you against — and what your product replaces vs. adds.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="access_heor" field="comparator" label="Payer-perspective comparator" value={data.comparator} onChange={setField("comparator")} onBlur={persist} placeholder="What the HTA body / payer will benchmark you against — often the current standard of care in this population, not the placebo used in your trial." context={data} testid="ah-comp" />
            <WandField tab="access_heor" field="replaces_cost" label="What current treatment or cost does it replace / reduce?" value={data.replaces_cost} onChange={setField("replaces_cost")} onBlur={persist} context={data} testid="ah-replaces" />
            <WandField tab="access_heor" field="additional_costs" label="What additional costs does it create?" value={data.additional_costs} onChange={setField("additional_costs")} onBlur={persist} context={data} testid="ah-addcosts" />
          </CardContent>
        </Card>

        {/* C. Payer evidence gaps */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><FileWarning className="w-4 h-4 text-teal-600" /> C · Payer evidence gaps</CardTitle>
            <p className="text-xs text-slate-500">Types of evidence: {EVIDENCE_TYPES.join(", ")}.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="access_heor" field="why_payer_fund" label="Why should a payer fund it rather than continue with current care?" value={data.why_payer_fund} onChange={setField("why_payer_fund")} onBlur={persist} rows={3} context={data} testid="ah-payer" />
            <div>
              <p className="text-sm font-medium text-slate-800 mb-2">Evidence table — what must be demonstrated, for whom, and do we have it?</p>
              <div className="hidden md:grid grid-cols-12 gap-3 px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wide border-b">
                <div className="col-span-6">What must be demonstrated?</div>
                <div className="col-span-3">Who needs it?</div>
                <div className="col-span-2">Do we have it?</div>
                <div className="col-span-1"></div>
              </div>
              {evidence.map((e) => (
                <div key={e.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 py-2 px-3 border-b last:border-b-0" data-testid={`ah-ev-${e.id}`}>
                  <Input value={e.what} onChange={(ev) => updateEv(e.id, { what: ev.target.value })} onBlur={persist} className="col-span-6 text-sm h-9" placeholder="e.g. Meaningful clinical benefit" data-testid={`ah-ev-what-${e.id}`} />
                  <Input value={e.who} onChange={(ev) => updateEv(e.id, { who: ev.target.value })} onBlur={persist} className="col-span-3 text-sm h-9" placeholder="Regulator / clinician / payer" data-testid={`ah-ev-who-${e.id}`} />
                  <div className="col-span-2">
                    <Select value={e.have} onValueChange={(v) => {
                      const nextEvidence = evidence.map((ev) => ev.id === e.id ? { ...ev, have: v } : ev);
                      const nextAh = { ...data, evidence: nextEvidence }; setData(nextAh); persistTab(nextAh);
                    }}>
                      <SelectTrigger className="h-9 text-xs" data-testid={`ah-ev-have-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{HAVE_OPTIONS.map((o) => <SelectItem key={o} value={o} className="text-xs capitalize">{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <button onClick={() => removeEv(e.id)} className="col-span-1 text-slate-300 hover:text-red-500 text-left" data-testid={`ah-ev-del-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <Button size="sm" variant="ghost" onClick={addEv} className="mt-2 text-teal-600" data-testid="ah-ev-add"><PlusCircle className="w-4 h-4 mr-1" /> Add evidence row</Button>
            </div>
          </CardContent>
        </Card>

        {/* Auto-curated NICE appraisals */}
        <Card data-testid="ah-nice-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-teal-600" /> Comparable NICE appraisals <Badge variant="outline" className="text-[10px] ml-1">Auto-curated</Badge></CardTitle>
              <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} className="h-7 px-2 text-teal-600 hover:bg-teal-50" data-testid="ah-refresh-btn" title="Refresh NICE appraisals">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Refresh suggestions</span>
              </Button>
            </div>
            <p className="text-xs text-slate-500">Real NICE technology-appraisal decisions in your class or a comparable one — what worked, what did not.</p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch(q, false)} placeholder="Or search a specific term (e.g. gene therapy SMA)" className="text-sm" data-testid="ah-search-input" />
              <Button size="sm" onClick={() => runSearch(q, false)} disabled={busy} data-testid="ah-search-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
            <div className="space-y-2">
              {(data.nice_appraisals || []).length === 0 && !busy && <p className="text-xs text-slate-400 italic">Fill in what outcome your product improves to auto-curate NICE appraisals.</p>}
              {busy && (data.nice_appraisals || []).length === 0 && <p className="text-xs text-teal-600 italic flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Auto-curating NICE appraisals…</p>}
              {(data.nice_appraisals || []).map((a) => (
                <div key={a.id} className="border rounded-lg p-3 bg-white" data-testid={`ah-nice-${a.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-slate-800 hover:text-teal-700 flex-1 leading-snug">{a.title} <ExternalLink className="w-3 h-3 inline text-slate-400" /></a>
                    {typeof a.relevance_score === "number" && a.relevance_score > 0 && <Badge variant="secondary" className="text-[10px] flex items-center gap-1"><Star className="w-2.5 h-2.5" />{a.relevance_score}</Badge>}
                    <button onClick={() => removeApp(a.id)} className="text-slate-300 hover:text-red-500" data-testid={`ah-nice-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {a.recommendation && <Badge variant="outline" className="text-[10px] mb-1">{a.recommendation}</Badge>}
                  {a.why_relevant && <p className="text-[11px] text-teal-700 italic mt-1">Why relevant: {a.why_relevant}</p>}
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] mt-2">
                    {[["Population", a.population], ["Comparator", a.comparator], ["Clinical uncertainty", a.clinical_uncertainty], ["Economic uncertainty", a.economic_uncertainty], ["Lesson", a.lesson]].map(([k, v]) => v && (
                      <div key={k}><dt className="text-slate-400">{k}</dt><dd className="text-slate-700">{v}</dd></div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-1"><CautionaryTale tale={CAUTIONARY_TALES.access_heor} testid="ah-cautionary" /></div>
    </div>
  );
}
